/**
 * Tidy-ish tree layout for the org chart: each subtree gets width = max(1, Σ child widths);
 * parents are centred above their children. Roots (no/unknown manager) sit side by side.
 */
export interface LayoutInput {
  id: string;
  managerId: string | null;
}

export function layoutTree(nodes: LayoutInput[], opts = { w: 240, h: 150 }) {
  const ids = new Set(nodes.map((n) => n.id));
  const children = new Map<string, string[]>();
  const roots: string[] = [];
  for (const n of nodes) {
    if (n.managerId && ids.has(n.managerId) && n.managerId !== n.id) {
      const list = children.get(n.managerId) ?? [];
      list.push(n.id);
      children.set(n.managerId, list);
    } else roots.push(n.id);
  }
  const width = new Map<string, number>();
  const seen = new Set<string>();
  const measure = (id: string): number => {
    if (seen.has(id)) return 1; // cycle guard
    seen.add(id);
    const kids = children.get(id) ?? [];
    const w = Math.max(
      1,
      kids.reduce((s, k) => s + measure(k), 0),
    );
    width.set(id, w);
    return w;
  };
  const pos = new Map<string, { x: number; y: number }>();
  const place = (id: string, left: number, depth: number, visiting: Set<string>) => {
    if (visiting.has(id)) return;
    visiting.add(id);
    const w = width.get(id) ?? 1;
    pos.set(id, { x: (left + w / 2 - 0.5) * opts.w, y: depth * opts.h });
    let cursor = left;
    for (const k of children.get(id) ?? []) {
      place(k, cursor, depth + 1, visiting);
      cursor += width.get(k) ?? 1;
    }
  };
  let left = 0;
  for (const r of roots) {
    measure(r);
    place(r, left, 0, new Set());
    left += width.get(r) ?? 1;
  }
  // Anything unreached (cycles) gets a row at the bottom.
  let extra = 0;
  for (const n of nodes) if (!pos.has(n.id)) pos.set(n.id, { x: extra++ * opts.w, y: -opts.h });
  return pos;
}
